import {
	ClerkLoaded,
	ClerkLoading,
	SignedIn,
	SignedOut,
} from "@clerk/clerk-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { MairkoLoader } from "./MairkoLoader.js";
import { PlatformSession } from "./PlatformSession.js";
import { SignInScreen } from "./SignInScreen.js";
import { readThemePreference, resolveTheme } from "./theme.js";

type Props = {
	children: ReactNode;
};

/** Centered wordmark while Clerk boots — same mark as empty home. */
function AuthBoot() {
	return (
		<div className="app-frame" aria-busy="true" aria-label="Loading">
			<div className="auth-boot auth-boot--loader">
				<MairkoLoader />
			</div>
		</div>
	);
}

function isClerkInvalidState(error: unknown): boolean {
	return error instanceof Error && error.message.includes("Invalid state");
}

/**
 * useAuth() throws when session/org ids are mid-update (billing checkout,
 * setActive). Catch that here so the desk remounts instead of a white screen.
 */
class ClerkStateBoundary extends Component<
	{ children: ReactNode },
	{ failed: boolean }
> {
	state = { failed: false };

	static getDerivedStateFromError(error: unknown): { failed: boolean } {
		if (isClerkInvalidState(error)) return { failed: true };
		throw error;
	}

	componentDidCatch(error: unknown, info: ErrorInfo): void {
		console.warn("[auth] Clerk session state", error, info.componentStack);
	}

	render(): ReactNode {
		if (this.state.failed) {
			return (
				<div className="app-frame" aria-busy="true">
					<div className="auth-boot auth-boot--loader">
						<MairkoLoader />
						<p className="auth-boot-copy">Refreshing your session…</p>
						<button
							type="button"
							className="btn btn-accent"
							onClick={() => this.setState({ failed: false })}
						>
							Retry
						</button>
					</div>
				</div>
			);
		}
		return this.props.children;
	}
}

/** Clerk identity only — workspace name is native (Jules / tenants.name). */
export function AuthGate({ children }: Props) {
	const resolved = resolveTheme(readThemePreference());

	return (
		<ClerkStateBoundary>
			<ClerkLoading>
				<AuthBoot />
			</ClerkLoading>
			<ClerkLoaded>
				<SignedOut>
					<div className="app-frame app-frame--auth">
						<SignInScreen resolved={resolved} />
					</div>
				</SignedOut>
				<SignedIn>
					<PlatformSession>{children}</PlatformSession>
				</SignedIn>
			</ClerkLoaded>
		</ClerkStateBoundary>
	);
}
